
import EventPublisher from "../utils/EventPublisher";
import SecureRequestExecuter from "./requestExecuters/SecureRequestExecuter";
import SecureResponseHandler from "./ResponseHandlers/SecureResponseHandler";
import ActionResultEvents from "../constants/events/ActionResultEvents";


export class Advertisement {
    constructor(
        advertisementId,
        memberId,
        title,
        description,
        statusId,
        statusName,
        created,
        updated,
        deleted,
        imageNames,
        isEditable
    ) {
        this.advertisementId = advertisementId;
        this.memberId = memberId;
        this.title = title;
        this.description = description;
        this.statusId = statusId;
        this.statusName = statusName;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
        this.imageNames = imageNames;
        this.isEditable = isEditable;
    }
}

class GetMyAdvertisementsResponse {
    constructor(openAdvertisements, closedAdvertisements) {
        this.openAdvertisements = openAdvertisements;
        this.closedAdvertisements = closedAdvertisements;
    }
}

class AdvertisementBillingReservation {
    constructor(advertisementId, advertisementTitle, spaceId, spaceTitle, startDate, endDate, created) {
        this.advertisementId = advertisementId;
        this.advertisementTitle = advertisementTitle;
        this.spaceId = spaceId;
        this.spaceTitle = spaceTitle;
        this.startDate = startDate;
        this.endDate = endDate;
        this.created = created;
    }
}

class PaidAdvertisementBillingReservation {
    constructor(advertisementBillingReservation, amount) {
        this.reservation = advertisementBillingReservation;
        this.amount = amount;
    }
}


export default class AdvertisementAPIClient {
    constructor() {
        this.submitAdvertisementResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_CREATED", this.onAdvertisementCreated));
        this.getAdvertisementHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_COLLECTED", this.onGetAdvertisementSuccess));
        this.getMyAdvertisementsHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENTS_COLLECTED", this.onGetMyAdvertisementsSuccess));
        this.openAdvertisementResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_OPENED", this.onAdvertisementStatusChange));
        this.closeAdvertisementResponseHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_CLOSED", this.onAdvertisementStatusChange));
        this.updateAdvertisementHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_UPDATED", this.onAdvertisementUpdated));
        this.getAdvertisementBillingHandler =
            new SecureResponseHandler(
                new Map()
                    .set("BILLING_LOADED", this.onGetAdvertisementBillingSuccess));
    }

    getAdvertisement(advertisementId) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/advertisement/"+advertisementId,
            {},
            {},
            this.getAdvertisementHandler);
    }

    getMyAdvertisements() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/my_advertisements",
            {},
            {},
            this.getMyAdvertisementsHandler);
    }


    submitAdvertisement(advertisementData) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/advertisement",
            {},
            {
                "title": advertisementData.title,
                "description": advertisementData.description,
                "image_names": advertisementData.imageNames
            },
            {},
            this.submitAdvertisementResponseHandler
        );
    }

    updateAdvertisement(advertisementData) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/advertisement",
            {},
            {
                "title": advertisementData.title,
                "description": advertisementData.description,
                "image_names": advertisementData.imageNames,
                "advertisement_id": advertisementData.advertisementId,
            },
            {},
            this.updateAdvertisementHandler
        );
    }

    openAdvertisement(advertisementId) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/advertisement/open",
            {},
            {
                "advertisement_id": advertisementId
            },
            {},
            this.openAdvertisementResponseHandler
        );
    }

    closeAdvertisement(advertisementId) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/advertisement/close",
            {},
            {
                "advertisement_id": advertisementId
            },
            {},
            this.closeAdvertisementResponseHandler
        );
    }

    getAdvertiserBillingForRange(startDate, endDate) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/billing",
            {
                "start_date": startDate,
                "end_date": endDate
            },
            {},
            this.getAdvertisementBillingHandler
        );
    }

    onGetAdvertisementSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.ADVERTISEMENT_LOADED,
            new Advertisement(
                responseBodyContents["advertisement_id"],
                responseBodyContents["member_id"],
                responseBodyContents["title"],
                responseBodyContents["description"],
                responseBodyContents["status_id"],
                responseBodyContents["status_name"],
                responseBodyContents["created"],
                responseBodyContents["updated"],
                responseBodyContents["deleted"],
                responseBodyContents["image_names"],
                responseBodyContents["isEditable"]));
    };

    onGetAdvertisementBillingSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        const paidAdvertisementBillingReservations = [];
        for (const paidReservation of responseBodyContents["paid_reservations"]) {
            paidAdvertisementBillingReservations.push(
                new PaidAdvertisementBillingReservation(
                    new AdvertisementBillingReservation(
                        paidReservation["reservation"]["advertisement_id"],
                        paidReservation["reservation"]["advertisement_title"],
                        paidReservation["reservation"]["space_id"],
                        paidReservation["reservation"]["space_title"],
                        paidReservation["reservation"]["start_date"],
                        paidReservation["reservation"]["end_date"],
                        paidReservation["reservation"]["activated"],
                    ),
                    paidReservation["amount"]
                )
            )
        }

        EventPublisher.publish(
            ActionResultEvents.ADVERTISEMENT_BILLING_LOADED,
            paidAdvertisementBillingReservations
        );
    };

    onGetMyAdvertisementsSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.MY_ADVERTISEMENTS_LOADED,
            new GetMyAdvertisementsResponse(
                responseBodyContents["open"].map(
                    advertisement => new Advertisement (
                        advertisement["advertisement_id"],
                        advertisement["member_id"],
                        advertisement["title"],
                        advertisement["description"],
                        advertisement["status_id"],
                        advertisement["status_name"],
                        advertisement["created"],
                        advertisement["updated"],
                        advertisement["deleted"],
                        advertisement["image_names"])),
                responseBodyContents["closed"].map(
                    advertisement => new Advertisement (
                        advertisement["advertisement_id"],
                        advertisement["member_id"],
                        advertisement["title"],
                        advertisement["description"],
                        advertisement["status_id"],
                        advertisement["status_name"],
                        advertisement["created"],
                        advertisement["updated"],
                        advertisement["deleted"],
                        advertisement["image_names"]))));
    };

    onAdvertisementCreated = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.ADVERTISEMENT_CREATED, {});
    };

    onAdvertisementStatusChange = (responseHeaders, responseBodyContents, dataFromAction) => {
        this.getMyAdvertisements();
    };

    onAdvertisementUpdated = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.ADVERTISEMENT_EDITED, {});
    }


}